import React, { useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { loginSuccess, setLoading, setError } from '../../store/slices/authSlice';
import api from '../../utils/api';
import { persistAuth } from '../../utils/auth';
import { toast } from 'react-toastify';
import { getMeetingDisplayName } from '../../utils/meetingDisplayName';
import OtpVerifyEmail from './OtpVerify';

// Animations & Icons
import { motion, AnimatePresence } from 'framer-motion';
import { FiMail, FiCheckCircle, FiShield, FiArrowRight, FiSend, FiAlertCircle, FiBell, FiInfo, FiBook } from 'react-icons/fi';
import {
  FaBookOpen, FaGraduationCap, FaChalkboardTeacher, FaDonate,
  FaPrayingHands, FaBroadcastTower, FaComments, FaBullhorn,
  FaUserTie, FaUsersCog, FaBookReader, FaGlobeAsia, FaUsers
} from "react-icons/fa";

/* ===== VISITOR CARDS CONFIG ===== */
const VISITOR_CARDS = [
  { key: "academic", title: "Academic Programs", desc: "Bible-centered degree & certificate programs", icon: <FaBookOpen />, color: "linear-gradient(135deg,#5f72be,#9bafd9)" },
  { key: "class", title: "Class Information", desc: "Online classes, schedules & faculty guidance", icon: <FaChalkboardTeacher />, color: "linear-gradient(135deg,#36d1dc,#5b86e5)" },
  { key: "apply", title: "Student Admission", desc: "Apply for theological and ministry studies", icon: <FaGraduationCap />, color: "linear-gradient(135deg,#f6d365,#fda085)" },
  { key: "faculty", title: "Faculty Application", desc: "Serve as a teacher, mentor or academic guide", icon: <FaUserTie />, color: "linear-gradient(135deg,#56ab2f,#a8e063)" },
  { key: "staff", title: "Staff Application", desc: "Join ministry administration & support teams", icon: <FaUsersCog />, color: "linear-gradient(135deg,#4ca1af,#c4e0e5)" },
  { key: "services", title: "Student Services", desc: "Digital library, worship & learning services", icon: <FaBookReader />, color: "linear-gradient(135deg,#8360c3,#2ebf91)" },
  { key: "donate", title: "Support the Ministry", desc: "Partner in training servants of God", icon: <FaDonate />, color: "linear-gradient(135deg,#f7971e,#ffd200)" },
  { key: "prayer", title: "Prayer Request", desc: "Submit your prayer needs & testimonies", icon: <FaPrayingHands />, color: "linear-gradient(135deg,#8e2de2,#4a00e0)" },
  { key: "live", title: "Live Worship", desc: "Watch live worship & teaching sessions", icon: <FaBroadcastTower />, color: "linear-gradient(135deg,#ff758c,#ff7eb3)" },
  { key: "notice", title: "Notice Board", desc: "Important announcements & updates", icon: <FaBullhorn />, color: "linear-gradient(135deg,#43cea2,#185a9d)" },
  { key: "ssd", title: "SSD Network", desc: "A Digital Network for Scripture & Faith", icon: <FaGlobeAsia />, color: "linear-gradient(135deg,#2193b0,#6dd5ed)" },
  { key: "chat", title: "Chat With Us", desc: "Guidance for admissions & programs", icon: <FaComments />, color: "linear-gradient(135deg,#11998e,#38ef7d)" }
];

const Login = () => {
  const [email, setEmail] = useState('');
  const [showOtp, setShowOtp] = useState(false);
  const [emailVerified, setEmailVerified] = useState(false);
  const [editingEmail, setEditingEmail] = useState(false);
  
  const [showRoleModal, setShowRoleModal] = useState(false);
  const [userData, setUserData] = useState(null);

  const { loading, error } = useSelector((state) => state.auth);
  const dispatch = useDispatch();
  const location = useLocation();
  const navigate = useNavigate();

  const isValidEmail = (e) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(e);

  const handleEmailChange = (e) => {
    setEmail(e.target.value);
    // setShowOtp(false);
    // setEmailVerified(true);
    // setEditingEmail(false);
  };

  const handleSendOtp = async () => {
    // if (isValidEmail(email)) {
    //   setShowOtp(true);
    // } else {
    //   toast.error("Please enter a valid email address");
    // }
   await handleLoginFlow();
  };

  const handleVerified = async () => {
    setEmailVerified(true);
    setShowOtp(false);
    setEditingEmail(false);
    toast.success("Email verified successfully");
    await handleLoginFlow();
  };

  const handleLoginFlow = async () => {
    dispatch(setLoading(true));
    dispatch(setError(null));
    try {
      const response = await api.post('/auth/login-email', { email });
      const user = response?.data?.user;
      
      sessionStorage.setItem('userName', getMeetingDisplayName(user, email));
      dispatch(loginSuccess(response.data));
      persistAuth(response.data, true); 
      setUserData(user);

      const userRole = user?.role?.toLowerCase() || 'visitor';
      const adminRoles = ['super_admin', 'admin', 'office', 'church_admin'];

      if (adminRoles.includes(userRole)) {
        finalizeLogin();
      } else {
        setShowRoleModal(true);
      }
      
    } catch (err) {
      dispatch(setError(err.response?.data?.message || 'Verification failed. Please try again.'));
      setEmailVerified(false); 
      setShowOtp(false);
    } finally {
      dispatch(setLoading(false));
    }
  };

  const finalizeLogin = () => {
    setShowRoleModal(false);
    const fromPath = location.state?.from?.pathname || '/';
    const fromSearch = location.state?.from?.search || '';
    navigate(`${fromPath}${fromSearch}`, { replace: true });
  };

  // ================= RENDER MODAL CONTENT (ALL ENGLISH WITH BSI TEXT) =================
  const renderModalContent = () => {
    const role = userData?.role?.toLowerCase() || 'visitor';

    return (
      <div className="p-4 md:p-6 text-gray-700">
        <div className="mb-6 p-4 rounded-xl bg-pink-50 border-l-4 border-[#7f083c] shadow-sm">
          <p className="font-semibold mb-0 leading-relaxed italic">
            "This application is designed for Video Meetings. Important notifications, session alerts, and academic information will be delivered to you through this platform in real-time."
          </p>
        </div>

        {/* --- STUDENT ROLE --- */}
        {['student', 'member'].includes(role) && (
          <motion.div initial={{ opacity: 0, x: -10 }} animate={{ opacity: 1, x: 0 }}>
            <h5 className="text-[#7f083c] font-bold flex items-center gap-2 mb-3">
              <FiBell /> Welcome, Dear Students
            </h5>
            <div className="space-y-3 text-sm leading-relaxed">
              <p>Your video meeting schedules and academic announcements will be sent to this app during working days. Please check your <strong>Notifications</strong> regularly to stay updated.</p>
              <p className="bg-gray-100 p-3 rounded-lg border border-gray-200">
                <FiInfo className="me-2 text-blue-600 inline" /> 
                To ensure a fruitful learning experience, please review the <strong>Student Handbook</strong> on your dashboard. It contains essential guidelines regarding virtual classroom conduct and academic integrity.
              </p>
              
              {/* BSI BIBLE TEXT FOR STUDENTS */}
              <div className="bg-gradient-to-r from-pink-50 to-white p-4 rounded-lg border-l-4 border-pink-500 shadow-sm mt-3">
                <div className="flex items-start gap-3">
                  <FiBook className="text-pink-600 mt-1 flex-shrink-0" size={18} />
                  <div>
                    <p className="italic text-gray-800 font-medium mb-2">
                      <span className="font-bold text-[#7f083c]">1 Timothy 4:12 (BSI):</span> "Let no one despise your youth, but be an example to the believers in word, in conduct, in love, in spirit, in faith, in purity."
                    </p>
                    <p className="italic text-gray-800 font-medium mb-0">
                      <span className="font-bold text-[#7f083c]">2 Timothy 2:15 (BSI):</span> "Be diligent to present yourself approved to God, a worker who does not need to be ashamed, rightly dividing the word of truth."
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </motion.div>
        )}

        {/* --- FACULTY & STAFF ROLE --- */}
        {['faculty', 'staff', 'pastor', 'elder', 'deacon'].includes(role) && (
          <motion.div initial={{ opacity: 0, x: -10 }} animate={{ opacity: 1, x: 0 }}>
            <h5 className="text-[#7f083c] font-bold flex items-center gap-2 mb-3">
              <FaChalkboardTeacher /> Welcome, Faculty & Staff
            </h5>
            <div className="space-y-3 text-sm">
              <p>We warmly welcome you to the ministry of Shepherds Biblical Web Seminary. Your dedication to teaching biblical truth and mentoring the next generation is vital to our mission.</p>
              <p>Please use this portal to access your assigned courses, manage video sessions, and communicate with your students effectively.</p>

              {/* BSI BIBLE TEXT FOR FACULTY */}
              <div className="bg-gradient-to-r from-blue-50 to-white p-4 rounded-lg border-l-4 border-blue-500 shadow-sm mt-3">
                <div className="flex items-start gap-3">
                  <FiBook className="text-blue-600 mt-1 flex-shrink-0" size={18} />
                  <p className="italic text-gray-800 font-medium mb-0">
                    <span className="font-bold text-[#7f083c]">James 3:1 (BSI):</span> "My brethren, let not many of you become teachers, knowing that we shall receive a stricter judgment."
                  </p>
                </div>
              </div>
            </div>
          </motion.div>
        )}

        {/* --- VISITORS ROLE --- */}
        {['visitor', 'visitors', 'guest', 'public', 'user'].includes(role) && (
          <motion.div initial={{ opacity: 0, x: -10 }} animate={{ opacity: 1, x: 0 }}>
            <h5 className="text-[#7f083c] font-bold flex items-center gap-2 mb-3">
              <FaUsers /> Welcome, Honored Visitors
            </h5>
            <p className="text-sm mb-4">We are delighted to have you explore our digital platform. You will receive <strong>Notifications</strong> for open sessions, prayer meetings, and Sunday worship services, where you are welcome to join us freely to exalt the name of the Lord.</p>
            
            {/* BSI BIBLE TEXT FOR VISITORS */}
            <div className="bg-gradient-to-r from-green-50 to-white p-4 rounded-lg border-l-4 border-green-500 shadow-sm mb-4">
              <div className="flex items-start gap-3">
                <FiBook className="text-green-600 mt-1 flex-shrink-0" size={18} />
                <p className="italic text-gray-800 font-medium mb-0">
                  <span className="font-bold text-[#7f083c]">Matthew 9:37 (BSI):</span> "Then He said to His disciples, 'The harvest truly is plentiful, but the laborers are few.'"
                </p>
              </div>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-3 gap-3 max-h-[40vh] overflow-y-auto p-2 border rounded-xl bg-gray-50">
              {VISITOR_CARDS.map((card) => (
                <div key={card.key} className="p-2 text-white rounded-lg shadow-sm text-center text-[10px] font-bold" style={{ background: card.color }}>
                  {card.title}
                </div>
              ))}
            </div>
          </motion.div>
        )}

        {/* --- COMMON NOTE FOR ALL --- */}
        <div className="mt-6 pt-4 border-t border-gray-200 text-center">
          <p className="text-[#7f083c] font-bold flex items-center justify-center gap-2 mb-2">
             <FiShield /> "Let us all cooperate fully with divine devotion and the fear of God to advance the work of His Kingdom."
          </p>
          <p className="text-sm italic text-gray-700 font-medium">
             <strong className="text-gray-900">Colossians 3:23 (BSI):</strong> "And whatever you do, do it heartily, as to the Lord and not to men."
          </p>
        </div>
      </div>
    );
  };

  return (
    <div className="min-h-screen flex flex-col items-center py-10 px-4 sm:px-6 lg:px-8 relative overflow-x-hidden overflow-y-auto" style={{ background: 'linear-gradient(135deg, #fff0f5 0%, #ffe4e6 100%)' }}>
      
      {/* Background Decorative Circles */}
      <div className="absolute top-[-10%] left-[-10%] w-96 h-96 bg-pink-300 rounded-full mix-blend-multiply filter blur-3xl opacity-40 animate-blob"></div>
      <div className="absolute bottom-[-10%] right-[-10%] w-96 h-96 bg-purple-300 rounded-full mix-blend-multiply filter blur-3xl opacity-40 animate-blob animation-delay-2000"></div>

      {/* ================= 1. HEADER SECTION ================= */}
      <motion.div initial={{ opacity: 0, y: -30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8 }} className="text-center z-10 flex flex-col items-center mt-2">
        <div className="bg-white p-2 rounded-full shadow-2xl mb-4 border border-pink-100">
          <img src="/favicon.ico" alt="Seminary Logo" className="w-20 h-20 md:w-24 md:h-24 rounded-full object-contain" />
        </div>
        <h1 className="text-2xl md:text-4xl font-extrabold text-[#7f083c] tracking-tight drop-shadow-sm mb-1 uppercase">SHEPHERDS BIBLICAL WEB SEMINARY</h1>
        <h3 className="text-xs md:text-sm font-semibold italic text-[#9a0a4a] tracking-widest opacity-90">SHEPHERDING THE SHEPHERDS</h3>
      </motion.div>

      {/* ================= 2. WELCOME CONTENT ================= */}
      <motion.div initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.8, delay: 0.3 }} className="text-center z-10 max-w-4xl mx-auto my-8 px-4">
        {/* FIXED THE TEXT OVERLAP BUG IN FIREFOX BY REMOVING BACKGROUND POSITION ANIMATION */}
        <h2 className="text-2xl md:text-4xl font-black bg-gradient-to-r from-[#7f083c] to-[#9a0a4a] bg-clip-text text-transparent mb-4">
          Equipping Leaders for Global Ministry
        </h2>
        <div className="bg-white/40 backdrop-blur-md p-5 rounded-2xl border border-white/60 shadow-sm">
          <p className="text-gray-800 text-sm md:text-base font-medium italic leading-relaxed">
            "Shepherds Biblical Web Seminary is not just an institution — it is a mission to bring Biblical education to every doorstep through digital means and raise leaders for the glory of God."
          </p>
        </div>
      </motion.div>

      {/* ================= 3. MAIN LOGIN CARD ================= */}
      <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.5 }} className="max-w-md w-full bg-white/95 backdrop-blur-xl p-8 rounded-3xl shadow-2xl z-10 border border-white mb-12">
        <div className="text-center mb-8 flex flex-col items-center">
          <div className="flex items-center justify-center mb-2">
            <motion.div animate={{ scale: [1, 1.15, 1] }} transition={{ duration: 2, repeat: Infinity }} className="text-[#7f083c] mr-3"><FiShield size={32} /></motion.div>
            <h3 className="text-2xl md:text-3xl font-black uppercase tracking-widest text-[#7f083c] pb-1">SECURE GATEWAY</h3>
          </div>
          <p className="text-sm text-gray-500 font-medium">Enter your registered email to continue</p>
        </div>
        
        {error && ( <div className="bg-red-50 border-l-4 border-red-500 text-red-700 p-3 rounded-md mb-6 text-sm flex items-center shadow-sm"><FiAlertCircle className="mr-2 text-lg flex-shrink-0" /> {error} </div> )}

        <div className="space-y-4">
          <div>
            <label htmlFor="email" className="block text-sm font-bold text-gray-700 mb-2">Email Address</label>
            <div className="relative group">
              <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none text-gray-400 group-focus-within:text-[#7f083c] transition-colors"><FiMail size={20} /></div>
              <input id="email" type="email" required disabled={showOtp || emailVerified} className={`block w-full pl-12 pr-4 py-3.5 bg-gray-50 border ${email && !isValidEmail(email) ? 'border-red-400' : 'border-gray-200'} rounded-xl outline-none text-gray-800 font-semibold`} value={email} onChange={handleEmailChange} placeholder="name@example.com" />
            </div>
          </div>

          <AnimatePresence>
            {/* {!showOtp && !emailVerified && isValidEmail(email) && ( */}
              <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: 20 }} className="pt-2">
                <button onClick={handleLoginFlow} className="w-full flex items-center justify-center py-3.5 px-4 rounded-xl shadow-lg text-white font-bold transition-all bg-gradient-to-r from-[#7f083c] to-[#9a0a4a] hover:shadow-xl transform hover:-translate-y-0.5">Verify Email <FiSend className="ml-2" /></button>
              </motion.div>
            {/* )} */}
          </AnimatePresence>

          <AnimatePresence>
            {/* {showOtp && !emailVerified && (
              <motion.div initial={{ opacity: 0, height: 'auto' }} animate={{ opacity: 1 }} className="pt-2">
                 <OtpVerifyEmail email={email} type="login" onVerified={handleVerified} onCancel={() => setShowOtp(false)} />
              </motion.div>
            )} */}
          </AnimatePresence>
{/* 
          {emailVerified && (
            <motion.div initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }} className="text-center pt-2">
               <div className="flex items-center justify-center text-green-700 font-bold mb-5 bg-green-50 py-3 rounded-xl border border-green-200 shadow-sm"><FiCheckCircle className="mr-2" size={20} /> Email Verified Successfully!</div>
               <button disabled={loading} onClick={handleLoginFlow} className="w-full flex items-center justify-center py-4 px-4 border border-transparent rounded-xl shadow-[0_10px_20px_rgba(127,8,60,0.3)] text-white font-bold bg-gradient-to-r from-[#7f083c] to-[#9a0a4a] hover:from-[#9a0a4a] hover:to-[#7f083c] transition-all text-lg tracking-wide transform hover:-translate-y-1">Access Dashboard <FiArrowRight className="ml-2" /></button>
            </motion.div>
          )} */}
        </div>
      </motion.div>

      {/* ================= ROLE BASED POPUP ================= */}
      <AnimatePresence>
        {showRoleModal && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm">
            <motion.div initial={{ opacity: 0, scale: 0.9, y: 30 }} animate={{ opacity: 1, scale: 1, y: 0 }} exit={{ opacity: 0, scale: 0.9, y: 30 }} className="bg-white w-full max-w-2xl rounded-3xl shadow-2xl overflow-hidden flex flex-col max-h-[90vh] border border-gray-100">
              <div className="bg-gradient-to-r from-[#7f083c] to-[#9a0a4a] p-5 text-white text-center shadow-md relative">
                <h3 className="text-2xl font-extrabold tracking-wide uppercase">Important Information</h3>
              </div>
              <div className="overflow-y-auto flex-grow bg-white">
                {renderModalContent()}
              </div>
              <div className="p-5 bg-gray-50 border-t border-gray-100 flex justify-end">
                <button onClick={finalizeLogin} className="px-8 py-3 bg-[#7f083c] hover:bg-[#9a0a4a] text-white font-bold rounded-xl shadow-lg transition-all transform hover:-translate-y-0.5 flex items-center text-lg">Agree & Continue <FiArrowRight className="ml-2" /></button>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

    </div>
  );
};

export default Login;
